import { Close } from '@mui/icons-material';
import { Modal } from '@mui/material';
import { CollapsedBar } from 'components/common/CollapsedBar/CollapsedBar';
import OpenSeadragon from 'openseadragon';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import '@recogito/annotorious-openseadragon/dist/annotorious.min.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import 'css/OpenSeaDragon.css';
import { viewerBaseConfig } from 'components/ImageViewer/viewerBaseConfig';

import {
  isSharedPageSelector,
  openSlideInFullscreenSelector,
  setOpenSlideInFullscreen,
} from 'store/slices/slidesListSlice/slidesListSlice';
import {
  addDefaultViewerEventListeners,
  addOverlaysToViewer,
  buildAnnotationByResponseData,
  createButtonGroupElement,
  createFixedZoomButton,
  createFullScreenButtonElement,
  createNavSlideButtonElement,
  createScreenshotButtonElement,
  createZoomIndicatorElement,
  getAnnotationByInnerID,
  getCreatedComment,
  getEditedComments,
  getQCOverlays,
  getRemovedComments,
  getUpdatedRawAnnotations,
  getZoomIndicatorText,
  handleViewerSlideScreenshot,
  haveSameAnnotationContent,
  DistanceMeasureManager,
  initAnnotorious,
  micronsPerPixelToPixelsPerMeter,
  qcScoreOverlaysExist,
  setRowSelectionVisible,
  updateAnnotationComments,
  handleInitExternalAnnotations,
} from 'components/ImageViewer/utilities';
import {
  ANNOTATIONS_COLOR_LEGEND_FIELD,
  ANNOTATIONS_OPACITY_FIELD,
  ANNOTATION_COLOR_FIELD,
  ANNOTATION_STROKE_WIDTH_FIELD,
  DEFAULT_ANNOTATION_DRAW_TOOL,
  ANNOTATION_UX_V2_FIELD,
  COLOR_PROFILE_FIELD,
  DISPLAYED_EXTERNAL_ANNOTATIONS,
  DISTANCE_MEASURE_ENABLED_FIELD,
  NAVIGATOR_SWITCH_FIELD,
  ZOOM_LIST_LABELS,
} from 'components/ImageViewer/constants';
import { annotationFormatter } from 'components/ImageViewer/annotorious/formatter';
import { useUnmountIgnore } from 'utilities/useUnmountIgnore';
import { OpenSeaDragonWithFabric } from 'components/ImageViewer/OpenSeaDragonFabric';
import { useViewerFilters } from 'components/utilities/hooks/slideViewer';
import { OpenSeaDragonHelper } from 'components/ImageViewer/OpenSeaDragonImageHelper';
import {
  currentUserPreferencesSelector,
  updatePreferences,
} from 'store/slices/userDetailsSlice';
import Box from '@mui/material/Box';
import { BrowserView } from 'react-device-detect';
import { useSimpleSlideImageViewStyles } from 'components/ImageViewer/styles';
import {
  selectAiosynModelsOpacity,
  selectAiraModelsOpacity,
  selectCurrentSlideAIModels,
  selectLastChangedAIModelOptions,
  selectQCModelsOpacity,
  selectSegmentationModelsOpacity,
  selectTissueModelsOpacity,
} from 'store/slices/aiToolsSlice';
import { AIDrawingManager } from 'components/AITools/utilities/display';
import {
  useExternalAnnotations,
  useKeyboardShortcuts,
  usePreventAnnotationSelectOnDrag,
  useViewerMode,
  useViewerModeKeyboardShortcuts,
  VIEWER_MODES,
} from 'components/ImageViewer/hooks';

import { ExtAnnoDrawingManager } from 'components/ImageViewer/extAnnoUtils';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';

import {
  useCreateSlideAnnotationCommentMutation,
  useCreateSlideAnnotationMutation,
  useDeleteSlideAnnotationCommentMutation,
  useDeleteSlideAnnotationMutation,
  useLazyGetSlideAnnotationsQuery,
  useUpdateSlideAnnotationCommentMutation,
  useUpdateSlideAnnotationMutation,
} from 'store/apis/annotationsApi';
import { CustomMultiSlideToolbar } from 'components/ImageViewer/components/CustomMultiSlideToolbar';
import { ViewerModeRail } from 'components/ImageViewer/components/ViewerModeRail/ViewerModeRail';
import { ViewerModeBanner } from 'components/ImageViewer/components/ViewerModeBanner/ViewerModeBanner';
import {
  createSyncViewerController,
  registerSyncViewer,
  unregisterSyncViewer,
} from 'components/ImageViewer/syncViewContext';
import {
  useLazyGetSharedSlideQuery,
  useLazyGetSlideMLDataQuery,
  useLazyGetSlideQuery,
} from 'store/apis/slidesApi';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import {
  FAIL_GET_SLIDE_ML_DATA,
  FETCH_SLIDE_ERROR_MESSAGE,
} from 'constants/errorMessages';
import {
  IHC_ANTIBODIES_BASE_URL,
  SHARED_SOCIAL_ORDER_PAR_URL,
} from 'constants/urls';

export const SimpleSlideView = (props) => {
  const {
    slide: slideProp,
    id,
    showNavigator,
    overlaysNumber,
    annotationsEnabled,
    annotationDrawTool,
    annotationDrawModeEnabled,
    showOverlay,
    isGT450ScannerFiltersEnabled,
    isBookmarkFeatureEnabled,
    scoreToDisplay,
    isDistanceMeasureSupported,
    CustomScreenshotButton = null,
    setAnnotationsCount,
    handleNextSlide,
    handlePreviousSlide,
    showSlidesNavigation,
    gridApi,
    setAnnotationsLegend,
  } = props;

  const preferences = useSelector(currentUserPreferencesSelector);
  const colorProfile = preferences[COLOR_PROFILE_FIELD];
  const openInFullPage = useSelector(openSlideInFullscreenSelector);
  const dispatch = useDispatch();
  const { showError } = useSnackbar();
  const [isNavigatorCollapsed, setIsNavigatorCollapsed] = useState(false);
  const [tileSources, setTileSources] = useState(slideProp?.dzi_access_url);

  const [slide, setSlide] = useState(null);
  const [slideOpened, setSlideOpened] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  // The annotation currently selected for editing in Annotorious, if any.
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  // Refs mirror the latest annotations/slide so the Annotorious event
  // handlers keep a stable identity across renders. Without this the
  // handlers change identity whenever `annotations` changes, which tears
  // down and rebuilds the whole Annotorious overlay on every
  // create/update/delete/comment.
  const annotationsRef = useRef(annotations);
  // Ids of drawn annotations whose create request is still in flight: they
  // exist on the Annotorious layer but not yet in `annotations`.
  const pendingCreatesRef = useRef(new Set());
  // The Annotorious instance whose layer was last loaded from `annotations`.
  const syncedAnnoRef = useRef(null);
  const slideRef = useRef(slide);
  const annotationColorPreference = preferences[ANNOTATION_COLOR_FIELD];
  const annotationStrokeWidthPreference =
    preferences[ANNOTATION_STROKE_WIDTH_FIELD];
  // The style used for *new* annotations, kept in a ref so the (stable)
  // creation handler always reads the latest color/width preferences.
  const annotationStyleRef = useRef({
    color: annotationColorPreference,
    strokeWidth: annotationStrokeWidthPreference,
  });

  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  useEffect(() => {
    slideRef.current = slide;
  }, [slide]);

  useEffect(() => {
    annotationStyleRef.current = {
      color: annotationColorPreference,
      strokeWidth: annotationStrokeWidthPreference,
    };
  }, [annotationColorPreference, annotationStrokeWidthPreference]);
  /* This is just for passing viewer down to CustomMultiSlideToolbar */
  const [viewerState, setViewerState] = useState(null);
  const [isFullPage, setIsFullPage] = useState(false);
  // keep real zoom that could be from 0 to Infinity. It changes on every
  // animation frame while zooming, so it lives in a ref and the zoom controls
  // are patched in place instead of re-rendering this component per frame.
  const zoomRef = useRef(0);
  const zoomControlsRef = useRef(null);
  // keep ratio of Maximum zoom calculated by OSD to MAX_ZOOM_LEVEL_FOR_BASE_VIEWER variable
  const [actualZoomRatio, setActualZoomRatio] = useState(0);
  const actualZoomRatioRef = useRef(0);
  actualZoomRatioRef.current = actualZoomRatio;
  const { classes } = useSimpleSlideImageViewStyles();
  const viewerRef = useRef(null);
  const annoRef = useRef(null);
  const aiDrawingRef = useRef(null);
  const extAnnoDrawingRef = useRef(null);
  const viewerMouseTracker = useRef(null);
  const syncControllerRef = useRef(null);
  const [anno, setAnno] = useState();
  const [openDimensions, setOpenDimensions] = useState(false);
  const navigatorId = `navigator-${id}`;
  const showFullNavigator = !isNavigatorCollapsed && showNavigator;
  const showCollapsedNavigator = isNavigatorCollapsed && showNavigator;
  const displayedAnnotations = preferences[DISPLAYED_EXTERNAL_ANNOTATIONS];
  const annotationsOpacity = preferences[ANNOTATIONS_OPACITY_FIELD];
  const colorLegend = preferences[ANNOTATIONS_COLOR_LEGEND_FIELD];
  const isAnnotationUxV2Enabled = preferences[ANNOTATION_UX_V2_FIELD];
  const [getSlideMLData] = useLazyGetSlideMLDataQuery();

  const lastChangedAIModelOptions = useSelector(
    selectLastChangedAIModelOptions,
  );
  const qcModelsOpacity = useSelector(selectQCModelsOpacity);
  const tissueModelsOpacity = useSelector(selectTissueModelsOpacity);
  const airaModelsOpacity = useSelector(selectAiraModelsOpacity);
  const aiosynOpacity = useSelector(selectAiosynModelsOpacity);
  const segmentationModelsOpacity = useSelector(
    selectSegmentationModelsOpacity,
  );
  const currentAIModels = useSelector(selectCurrentSlideAIModels);

  const { pathname } = useLocation();

  const isIHC = pathname.includes(IHC_ANTIBODIES_BASE_URL);
  const isSharedSocialUrl = pathname.includes(SHARED_SOCIAL_ORDER_PAR_URL);

  const isSharedPage = useSelector(isSharedPageSelector);

  const updateColorLegend = (newPreferences) =>
    dispatch(
      updatePreferences({
        ...preferences,
        ...newPreferences,
      }),
    );

  const [createSlideAnnotation] = useCreateSlideAnnotationMutation();
  const [createSlideAnnotationComment] =
    useCreateSlideAnnotationCommentMutation();
  const [deleteSlideAnnotationComment] =
    useDeleteSlideAnnotationCommentMutation();
  const [updateSlideAnnotationComment] =
    useUpdateSlideAnnotationCommentMutation();
  const [updateSlideAnnotation] = useUpdateSlideAnnotationMutation();
  const [deleteSlideAnnotation] = useDeleteSlideAnnotationMutation();
  const [getSlideAnnotations] = useLazyGetSlideAnnotationsQuery();
  const [getSlideData, { data: slideData, error: getSlideDataError }] =
    useLazyGetSlideQuery();
  const [
    getSharedSlideData,
    { data: sharedSlideData, error: getSharedSlideError },
  ] = useLazyGetSharedSlideQuery();

  useQueryError(
    getSlideDataError || getSharedSlideError,
    FETCH_SLIDE_ERROR_MESSAGE,
  );

  // Hold off building the viewer until the slide details are in (or failed
  // to load), so it is built once per slide instead of once with no slide
  // and again when the details arrive.
  const isSlideReady = !!slide || !!getSlideDataError || !!getSharedSlideError;

  useEffect(() => {
    if (!slideProp) return;
    if (isIHC || isSharedSocialUrl) {
      setSlide(slideProp);

      return;
    }

    const slideGetter = isSharedPage ? getSharedSlideData : getSlideData;
    const fetchParams = isSharedPage
      ? { shareUUID: slideProp?.share_uuid }
      : { slideUUID: slideProp?.uuid };
    setSlide(null);
    slideGetter(fetchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideProp?.uuid, isIHC, isSharedSocialUrl]);

  useEffect(() => {
    if (!slideData && !sharedSlideData) return;
    setSlide(slideData || sharedSlideData);
  }, [slideData, sharedSlideData]);

  const modelsOpacitiesRef = useRef({
    qc: qcModelsOpacity,
    tissue: tissueModelsOpacity,
    segmentation: segmentationModelsOpacity,
    aira: airaModelsOpacity,
    aiosyn: aiosynOpacity,
  });

  const unmountIgnore = useUnmountIgnore();

  const isMeasureEnabled =
    isDistanceMeasureSupported && preferences[DISTANCE_MEASURE_ENABLED_FIELD];
  const distanceMeasureManager = useRef(null);

  const { mode, setMode } = useViewerMode();
  // Mirrors `mode` for handlers that are registered once on the OSD viewer.
  const modeRef = useRef(mode);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const isCrosshairCursor = isMeasureEnabled || mode === VIEWER_MODES.DRAW;

  const annotationAttachments = useExternalAnnotations(
    displayedAnnotations,
    slideProp?.external_annotations,
    showError,
  );

  useEffect(() => {
    modelsOpacitiesRef.current = {
      qc: qcModelsOpacity,
      tissue: tissueModelsOpacity,
      segmentation: segmentationModelsOpacity,
      aira: airaModelsOpacity,
      aiosyn: aiosynOpacity,
    };
  }, [
    qcModelsOpacity,
    tissueModelsOpacity,
    segmentationModelsOpacity,
    airaModelsOpacity,
    aiosynOpacity,
  ]);

  useEffect(() => {
    const distanceMeasureManagerInstance = distanceMeasureManager.current;

    if (distanceMeasureManagerInstance) {
      distanceMeasureManagerInstance.isMeasureEnabled = isMeasureEnabled;

      // Leaving measure mode (Esc, or toggling the tool off) must also drop a
      // half-started measurement instead of leaving a stray line on the slide.
      if (!isMeasureEnabled && distanceMeasureManagerInstance.isMeasuring) {
        distanceMeasureManagerInstance.cancelMeasuring();
      }
    }
  }, [isMeasureEnabled]);

  useViewerFilters(
    viewerState,
    isGT450ScannerFiltersEnabled,
    slide,
    colorProfile,
  );

  const handleScreenshot = (_, resizeValues = null) => {
    handleViewerSlideScreenshot(
      slideProp,
      viewerRef.current,
      distanceMeasureManager.current,
      resizeValues,
      extAnnoDrawingRef.current,
      aiDrawingRef.current,
      displayedAnnotations,
    );
    setOpenDimensions(false);
  };

  const getIsFullScreen = (currentViewerState) => {
    try {
      const isFullScreen = currentViewerState?.isFullPage?.();

      return isFullScreen;
    } catch (e) {
      return false;
    }
  };

  const toggleFullScreen = () => {
    setOpenDimensions(false);
    const isFullScreen = getIsFullScreen(viewerState);
    viewerState.setFullPage(!isFullScreen);

    if (!isFullScreen || !gridApi) return;

    setRowSelectionVisible(gridApi);
  };

  useKeyboardShortcuts(
    viewerState,
    toggleFullScreen,
    showSlidesNavigation,
    handleScreenshot,
    handlePreviousSlide,
    handleNextSlide,
  );

  const handleAnnotationCreate = useCallback(
    async (annotation) => {
      const { uuid: slideUUID } = slideRef.current;
      annotation['target']['source'] = window.location.href;
      // Persist the style the annotation was drawn with so the formatter can
      // render it identically after a reload (and so styles are per-annotation,
      // not global).
      annotation.style = annotation.style || {
        color: annotationStyleRef.current.color,
        strokeWidth: annotationStyleRef.current.strokeWidth,
      };

      const postParams = {
        data: annotation,
      };

      pendingCreatesRef.current.add(annotation.id);

      try {
        const response = await createSlideAnnotation({ slideUUID, postParams });

        if (response.error || !response.data) {
          throw response.error || new Error('Empty create response');
        }

        // check if component is still mounted
        if (!unmountIgnore.current) {
          setAnnotations((prevAnnotations) => [
            ...prevAnnotations,
            response.data,
          ]);
          // Update the count locally instead of refetching all annotations
          setAnnotationsCount?.({
            slideUUID,
            count: annotationsRef.current.length + 1,
          });
        }
      } catch (error) {
        // The shape was never persisted: do not leave it on the slide.
        annoRef.current?.removeAnnotation(annotation.id);
        showError('Failed to create annotation');
      } finally {
        pendingCreatesRef.current.delete(annotation.id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unmountIgnore],
  );

  const handleAnnotationCommentCreate = useCallback(
    async (annotation, comment) => {
      const persistedAnnotation = getAnnotationByInnerID(
        annotationsRef.current,
        annotation.id,
      );

      if (comment && persistedAnnotation) {
        const { uuid: annotationUUID } = persistedAnnotation;

        const postParams = {
          ...comment,
        };

        const response = await createSlideAnnotationComment({
          annotationUUID,
          slideUUID: slideRef.current?.uuid,
          postParams,
        });

        // Check if component is still mounted
        if (!unmountIgnore.current) {
          setAnnotations((prevAnnotations) => {
            const rawAnnotation = getAnnotationByInnerID(
              prevAnnotations,
              annotation.id,
            );

            // The annotation was deleted while its comment was being saved.
            if (!rawAnnotation) {
              return prevAnnotations;
            }

            const filteredAnnotations = prevAnnotations.filter(
              ({ data }) => data.id !== annotation.id,
            );
            const updatedAnnotation = updateAnnotationComments(
              rawAnnotation,
              response.data,
            );

            return [...filteredAnnotations, updatedAnnotation];
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unmountIgnore],
  );

  const updateAnnotation = useCallback(
    (annotation) => {
      const rawAnnotation = getAnnotationByInnerID(
        annotationsRef.current,
        annotation.id,
      );

      if (!rawAnnotation) {
        return;
      }

      const { uuid: annotationUUID } = rawAnnotation;
      const { uuid: slideUUID } = slideRef.current;
      const postParams = {
        data: annotation,
      };

      updateSlideAnnotation({ annotationUUID, slideUUID, postParams })
        .unwrap()
        .then(() => {
          setAnnotations((prevAnnotations) => {
            const updatedAnnotations = annoRef.current.getAnnotations();

            return getUpdatedRawAnnotations(
              prevAnnotations,
              updatedAnnotations,
              annotation,
            );
          });
        })
        .catch(() => {
          showError('Failed to update annotation');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleAnnotationCommentsEdit = useCallback((annotation, comments) => {
    if (comments.length) {
      comments.forEach((comment) => {
        const { uuid, value } = comment;
        updateSlideAnnotationComment({
          commentUUID: uuid,
          data: { value },
        })
          .unwrap()
          .then(() => {
            setAnnotations((prevAnnotations) => {
              const updatedAnnotations = annoRef.current.getAnnotations();

              return getUpdatedRawAnnotations(
                prevAnnotations,
                updatedAnnotations,
                annotation,
              );
            });
          })
          .catch(() => {
            showError('Failed to update annotation comment');
          });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnnotationCommentsRemove = useCallback((annotation, comments) => {
    if (comments.length) {
      comments.forEach((comment) => {
        deleteSlideAnnotationComment({ commentUUID: comment?.uuid })
          .unwrap()
          .then(() => {
            setAnnotations((prevAnnotations) => {
              const updatedAnnotations = annoRef.current.getAnnotations();

              return getUpdatedRawAnnotations(
                prevAnnotations,
                updatedAnnotations,
                annotation,
              );
            });
          })
          .catch(() => {
            showError('Failed to delete annotation comment');
          });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnnotationUpdate = useCallback(
    (annotation, prevAnnotation) => {
      const { value: currentPosition } = annotation.target.selector;
      const { value: prevPosition } = prevAnnotation.target.selector;

      if (currentPosition !== prevPosition) {
        updateAnnotation(annotation);
      }

      // there is no option to create more than one comment per one annotation update
      const createdComment = getCreatedComment(annotation, prevAnnotation);
      handleAnnotationCommentCreate(annotation, createdComment);

      const removedComments = getRemovedComments(annotation, prevAnnotation);
      handleAnnotationCommentsRemove(annotation, removedComments);

      const editedComments = getEditedComments(annotation, prevAnnotation);
      handleAnnotationCommentsEdit(annotation, editedComments);
    },
    [
      handleAnnotationCommentCreate,
      handleAnnotationCommentsRemove,
      handleAnnotationCommentsEdit,
      updateAnnotation,
    ],
  );

  const handleAnnotationRemove = useCallback(
    async (annotation) => {
      const { uuid: slideUUID } = slideRef.current;
      const rawAnnotation = getAnnotationByInnerID(
        annotationsRef.current,
        annotation.id,
      );

      // Already gone (deleted twice, or never persisted): nothing to delete.
      if (!rawAnnotation) {
        return;
      }

      const { uuid: annotationUUID } = rawAnnotation;

      deleteSlideAnnotation({ annotationUUID, slideUUID })
        .unwrap()
        .then(() => {
          setAnnotations((prevAnnotations) => {
            const updatedAnnotations = annoRef.current.getAnnotations();

            return getUpdatedRawAnnotations(
              prevAnnotations,
              updatedAnnotations,
              annotation,
            );
          });
          // Update the count locally instead of refetching all annotations
          const remainingCount = annotationsRef.current.length - 1;
          setAnnotationsCount?.(
            remainingCount ? { slideUUID, count: remainingCount } : null,
          );
        })
        .catch(() => {
          showError('Failed to delete annotation');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unmountIgnore],
  );

  /**
   * Fetch annotations effect. When either annotationsEnabled or slide
   * changes, it fetches annotations and set them to the state
   */
  useEffect(() => {
    if (!slide) {
      return;
    }
    getSlideAnnotations({ slideUUID: slide?.uuid }).then((response) => {
      if (!response) return;
      // Check if component is still mounted
      if (response.data?.length) {
        setAnnotationsCount?.({
          slideUUID: slide?.uuid,
          count: response.data.length,
        });
      }

      if (!unmountIgnore.current) {
        setAnnotations(response.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide?.uuid]);

  const openDimensionsHandler = (isOpen) => {
    setOpenDimensions(isOpen);
  };

  // The toolbar buttons are built rarely, so they call through this ref to
  // always reach the current handlers rather than the ones from build time.
  const toolbarHandlersRef = useRef({});
  toolbarHandlersRef.current = {
    handleScreenshot,
    openDimensionsHandler,
    toggleFullScreen,
    handlePreviousSlide,
    handleNextSlide,
  };
  function callToolbarHandler(name) {
    return (...args) => toolbarHandlersRef.current[name]?.(...args);
  }

  const updateZoomControls = useCallback(() => {
    const controls = zoomControlsRef.current;

    if (!controls) return;

    const zoom = zoomRef.current;
    const ratio = actualZoomRatioRef.current;
    const zoomLabel = zoom / ratio;

    controls.indicator.innerText = getZoomIndicatorText(zoom, ratio);
    controls.zoomButtons.forEach(({ value, element }) => {
      element.classList.toggle(
        'osd-zoom-btn-selected',
        Math.abs(value - zoomLabel) < Number.EPSILON,
      );
    });
  }, []);

  const handleZoomChange = useCallback(
    (value) => {
      zoomRef.current = value;
      updateZoomControls();
    },
    [updateZoomControls],
  );

  useEffect(() => {
    if (!viewerState) {
      return;
    }

    const zoomButtonList = ZOOM_LIST_LABELS.map((zoomButtonValue) =>
      createFixedZoomButton(
        viewerState,
        zoomButtonValue,
        actualZoomRatio,
        false,
      ),
    );

    const screenshotButton = new OpenSeadragon.Button({
      element: createScreenshotButtonElement(
        callToolbarHandler('handleScreenshot'),
        callToolbarHandler('openDimensionsHandler'),
        classes,
        openDimensions,
      ),
      tooltip: 'Screenshot',
    });

    const fullScreenButton = new OpenSeadragon.Button({
      element: createFullScreenButtonElement(
        id,
        callToolbarHandler('toggleFullScreen'),
        isFullPage,
        classes,
      ),
      tooltip: 'Full screen',
    });

    const prevSlideButton = new OpenSeadragon.Button({
      element: createNavSlideButtonElement(
        'prev',
        callToolbarHandler('handlePreviousSlide'),
        isFullPage,
        callToolbarHandler('toggleFullScreen'),
      ),
      tooltip: 'Previous slide',
    });

    const nextSlideButton = new OpenSeadragon.Button({
      element: createNavSlideButtonElement(
        'next',
        callToolbarHandler('handleNextSlide'),
        isFullPage,
        callToolbarHandler('toggleFullScreen'),
      ),
      tooltip: 'Next slide',
    });

    const buttonGroup = new OpenSeadragon.ButtonGroup({
      buttons: zoomButtonList,
      element: createButtonGroupElement(),
    });

    const zoomIndicator = new OpenSeadragon.Button({
      element: createZoomIndicatorElement(zoomRef.current, actualZoomRatio),
      tooltip: 'Zoom level',
    });

    zoomControlsRef.current = {
      indicator: zoomIndicator.element,
      zoomButtons: zoomButtonList.map((button, index) => ({
        value: ZOOM_LIST_LABELS[index],
        element: button.element,
      })),
    };
    updateZoomControls();

    const infoGroup = new OpenSeadragon.ButtonGroup({
      buttons: CustomScreenshotButton
        ? [zoomIndicator]
        : [zoomIndicator, screenshotButton],
      element: createButtonGroupElement(),
    });

    const navGroup = new OpenSeadragon.ButtonGroup({
      buttons: [prevSlideButton, nextSlideButton],
      element: createButtonGroupElement(),
    });

    // Remove old zoom controls
    if (viewerState.controls) {
      [...viewerState.controls].forEach((control) => {
        if (!control.element.id.includes('navigator-')) {
          viewerState.removeControl(control.element);
        }
      });
    }

    viewerState.addControl(buttonGroup.element, {
      attachToViewer: true,
      anchor: OpenSeadragon.ControlAnchor.TOP_LEFT,
    });
    viewerState.addControl(infoGroup.element, {
      attachToViewer: true,
      anchor: OpenSeadragon.ControlAnchor.TOP_LEFT,
    });
    if (showSlidesNavigation) {
      viewerState.addControl(navGroup.element, {
        attachToViewer: true,
        anchor: OpenSeadragon.ControlAnchor.BOTTOM_RIGHT,
      });
    }
    viewerState.addControl(fullScreenButton.element, {
      attachToViewer: true,
      anchor: OpenSeadragon.ControlAnchor.TOP_RIGHT,
      autoFade: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewerState,
    isFullPage,
    actualZoomRatio,
    openDimensions,
    displayedAnnotations,
  ]);
  /**
   * Init OpenSeadragon effect. It is triggered
   * when either of followings change:
   * - id
   * - tileSources
   * - showNavigator
   * Showing/hiding annotations is handled by the Annotorious effect below on
   * the live viewer; it must not rebuild the viewer (that blanked the slide
   * for a couple of seconds on every toggle).
   */

  useEffect(() => {
    if (id && isSlideReady) {
      const zoomInName = `zoom-in-${id}`;
      const zoomOutName = `zoom-out-${id}`;
      const fullPageName = `full-page-${id}`;
      const toolbarName = `toolbar-${id}`;
      const pixelsPerMeter = micronsPerPixelToPixelsPerMeter(
        slide?.microns_per_pixel,
      );

      const viewer = new OpenSeaDragonWithFabric({
        ...viewerBaseConfig,
        id,
        defaultZoomLevel: 1,
        zoomInButton: zoomInName,
        zoomOutButton: zoomOutName,
        fullPageButton: fullPageName,
        tileSources,
        // since the dzi is on a separate server, necessary for downloading screenshots
        crossOriginPolicy: 'Anonymous',
        // TODO - This isn't documented very well, but turn it on after you test on mobile
        // immediateRender: true,
        showNavigator: showFullNavigator,
        navigatorId,
        toolbar: toolbarName,
        pixelsPerMeter,
        // openseadragon allows us to pass our own custom attributes to the viewer
        isOmeTiff: !!slide?.name?.endsWith('.ome.tiff'),
      });

      setIsFullPage(false);
      viewer.addHandler('full-page', (event) => setIsFullPage(event.fullPage));

      if (openInFullPage) {
        viewer.setFullPage(true);
        dispatch(setOpenSlideInFullscreen(false));
      }

      if (isBookmarkFeatureEnabled) {
        viewer.bookmarkUrl({ id });
      }

      const viewerHelper = new OpenSeaDragonHelper({ viewer });

      viewer.scalebar({
        barThickness: 2,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        stayInsideImage: false,
      });

      viewerRef.current = viewer;

      viewerMouseTracker.current = new OpenSeadragon.MouseTracker({
        element: document,
      });

      addDefaultViewerEventListeners(viewerRef.current, {
        setSlideOpened,
        setZoom: handleZoomChange,
        actualZoomRatio,
        setActualZoomRatio,
        mouseTracker: viewerMouseTracker.current,
        viewerHelper,
      });

      setViewerState(viewer);

      const controller = createSyncViewerController({ id, viewer });
      const handleViewportChange = () => controller.handleViewportChange();

      syncControllerRef.current = { controller, handleViewportChange };
      registerSyncViewer(id, controller);
      viewer.addHandler('viewport-change', handleViewportChange);

      const isDistanceMeasurementAvailable = true;

      if (isDistanceMeasurementAvailable) {
        distanceMeasureManager.current = new DistanceMeasureManager(
          viewerRef.current,
          isMeasureEnabled,
        );
        distanceMeasureManager.current.init();
      }
    }

    return () => {
      if (syncControllerRef.current) {
        viewerRef.current?.removeHandler(
          'viewport-change',
          syncControllerRef.current.handleViewportChange,
        );
        unregisterSyncViewer(id);
        syncControllerRef.current = null;
      }

      const navigatorElement = viewerRef.current?.navigator?.element;
      const navigatorWrapper = navigatorElement?.parentNode?.parentNode;

      // OpenSeadragon removes the DOM nodes it was given when destroyed, and
      // the navigator/toolbar nodes are rendered by React. A teardown race
      // must not escalate into the app's error boundary.
      try {
        viewerRef.current?.navigator?.destroy();
        viewerRef.current?.destroy();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.debug('OpenSeadragon teardown skipped a node:', error.message);
      }
      viewerRef.current = null;

      if (
        navigatorElement &&
        navigatorWrapper &&
        !navigatorElement.parentNode
      ) {
        navigatorElement.className = 'osd-navigator-container rounded-border';
        navigatorWrapper.appendChild(navigatorElement);
      }

      distanceMeasureManager.current?.destroy();
      viewerMouseTracker.current?.destroy();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    showFullNavigator,
    tileSources,
    displayedAnnotations,
    // the uuid, not the slide object: a refetch of the same slide returns a
    // new object and must not tear down and rebuild the viewer
    slide?.uuid,
    isSlideReady,
  ]);

  useEffect(() => {
    if (!slideOpened || !slide) return undefined;

    const handleMLDataGetFail = () => {
      showError(FAIL_GET_SLIDE_ML_DATA);
    };

    aiDrawingRef.current = new AIDrawingManager(
      viewerRef.current,
      slide,
      modelsOpacitiesRef,
      getSlideMLData,
      handleMLDataGetFail,
      currentAIModels,
    );

    extAnnoDrawingRef.current = new ExtAnnoDrawingManager(
      viewerRef.current,
      slide,
      [],
    );

    return () => {
      aiDrawingRef.current?.destroy();
      aiDrawingRef.current = null;
      extAnnoDrawingRef.current?.destroy();
      extAnnoDrawingRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideOpened, slide]);

  const handleSelectionChange = useCallback(
    (annotation) => {
      if (!unmountIgnore.current) {
        setSelectedAnnotation(annotation || null);
      }
    },
    [unmountIgnore],
  );

  /**
   * Init Annotorious effect. The annotation event handlers have stable
   * identities (they read the latest state from refs), so this effect only
   * re-runs when the real triggers change:
   * - displayedAnnotations
   * - annotationDrawModeEnabled
   * - annotationDrawTool
   * - annotationsEnabled
   * (Previously it also re-ran on every annotation change because the
   * handler identities depended on `annotations`, which tore down and
   * rebuilt the whole overlay on each create/update/delete/comment.)
   */
  useEffect(() => {
    const viewer = viewerRef.current;
    // Right after the viewer is (re)built viewerState still holds the previous
    // instance; wait for the render that catches up so this binds once, to
    // the live viewer.
    const isViewerCurrent = !!viewer && viewer === viewerState;

    if (annotationsEnabled && isViewerCurrent) {
      annoRef.current = initAnnotorious({
        viewer,
        annotationsEnabled,
        annotationDrawTool,
        annotationDrawModeEnabled,
        formatter: annotationFormatter,
        getDrawStyle: () => annotationStyleRef.current,
        handleAnnotationUpdate,
        handleAnnotationCreate,
        handleAnnotationRemove,
        handleSelectionChange,
      });

      setAnno(annoRef.current);
    }

    return () => {
      annoRef.current?.destroy();
      annoRef.current = null;
      handleSelectionChange(null);
    };
  }, [
    viewerState,
    annotationsEnabled,
    // The OSD viewer is recreated when the slide/tiles change; re-create the
    // Annotorious overlay against the new viewer instead of leaving it
    // attached to the destroyed one.
    viewerState,
    handleAnnotationCreate,
    handleAnnotationRemove,
    handleAnnotationUpdate,
    handleSelectionChange,
  ]);

  usePreventAnnotationSelectOnDrag({ viewerRef, annoRef, modeRef });

  // Delete/Backspace on the selected annotation: close the editor and drop the
  // shape from the layer the way the editor's own delete button does, then
  // delete it on the server.
  const handleDeleteSelected = useCallback(
    (annotation) => {
      annoRef.current?.cancelSelected();
      annoRef.current?.removeAnnotation(annotation.id);
      handleAnnotationRemove(annotation);
    },
    [handleAnnotationRemove],
  );

  useViewerModeKeyboardShortcuts({
    mode,
    setMode,
    annoRef,
    onDeleteSelected: handleDeleteSelected,
  });

  // The banner's Exit button does what Esc does: leave the tool first, then
  // drop the selection.
  const handleExitAnnotationMode = useCallback(() => {
    if (mode !== VIEWER_MODES.SELECT) {
      setMode(VIEWER_MODES.SELECT);

      return;
    }

    annoRef.current?.cancelSelected();
  }, [mode, setMode]);

  /**
   * slideChanged effect. Whenever slide props changes,
   * it updates slide and tileSources states.
   */
  useEffect(() => {
    if (!slideProp) {
      return;
    }
    setTileSources(slideProp.dzi_access_url);
    if (!aiDrawingRef?.current || !extAnnoDrawingRef?.current) {
      return;
    }

    aiDrawingRef.current.updateSlide(slide);
    extAnnoDrawingRef.current.updateSlide(slide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide]);

  /**
   * toggleOverlay and redrawOverlay effects. When showOverlay or slideOpened
   * change, it toggles overlay. Additionally, it redraws overlay if slide or
   * overlaysNumber change
   */
  useEffect(() => {
    const isViewerUpdatable = slideOpened && viewerRef.current;

    if (!isViewerUpdatable || !viewerRef.current?.source) return;

    if (!showOverlay) viewerRef.current?.clearOverlays();
    else {
      viewerRef.current?.clearOverlays();

      if (qcScoreOverlaysExist(slide, scoreToDisplay)) {
        const qcOverlays = getQCOverlays(
          viewerRef.current,
          slide,
          scoreToDisplay,
          overlaysNumber,
        );

        addOverlaysToViewer(viewerRef.current, qcOverlays);
      }
    }
  }, [overlaysNumber, showOverlay, slide, slideOpened, scoreToDisplay]);

  /**
   * Keep the Annotorious layer in sync with the annotations state.
   *
   * The first sync of an Annotorious instance loads the whole list. After
   * that the layer is reconciled in place: re-initialising it on every change
   * (each create, comment, delete) stops the drawing tool, which silently
   * drops a drag that is in progress while the previous shape's create
   * request completes, and re-renders every shape.
   */
  useEffect(() => {
    const annotorious = annoRef.current;

    if (!annotations || !annotorious) return;

    const desiredAnnotations = annotations
      .filter((annotation) => annotation)
      .map(buildAnnotationByResponseData);

    if (syncedAnnoRef.current !== annotorious) {
      syncedAnnoRef.current = annotorious;
      annotorious.setAnnotations(desiredAnnotations);

      return;
    }

    const layerAnnotations = new Map(
      annotorious
        .getAnnotations()
        .map((layerAnnotation) => [layerAnnotation.id, layerAnnotation]),
    );

    desiredAnnotations.forEach((annotation) => {
      const layerAnnotation = layerAnnotations.get(annotation.id);

      if (
        !layerAnnotation ||
        !haveSameAnnotationContent(layerAnnotation, annotation)
      ) {
        annotorious.addAnnotation(annotation);
      }
    });

    const desiredIds = new Set(
      desiredAnnotations.map((annotation) => annotation.id),
    );

    layerAnnotations.forEach((layerAnnotation, annotationId) => {
      // A shape whose create request is still in flight stays on the slide.
      if (
        !desiredIds.has(annotationId) &&
        !pendingCreatesRef.current.has(annotationId)
      ) {
        annotorious.removeAnnotation(annotationId);
      }
    });
  }, [annotations, anno]);

  useEffect(() => {
    if (!slideOpened) {
      return;
    }

    const { modelName, changeType } = lastChangedAIModelOptions || {};

    if (modelName) {
      aiDrawingRef.current?.handleModelChange(changeType, modelName);
    }
  }, [lastChangedAIModelOptions, slide, slideOpened]);

  const osdDivClass = 'ocd-div';
  useEffect(() => {
    if (!viewerRef?.current?.source) {
      return;
    }
    if (annotationAttachments?.length) {
      displayedAnnotations.forEach((uuid) => {
        const { data } =
          annotationAttachments.find(
            (attachment) => attachment.uuid === uuid,
          ) || {};
        handleInitExternalAnnotations({
          extAnnoDrawingRef,
          data,
          uuid,
          setAnnotationsLegend,
          annotationOpacity: annotationsOpacity[uuid] || 100,
          updateColorLegend,
          colorLegend,
          mpp: slideProp?.microns_per_pixel,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayedAnnotations,
    viewerRef?.current?.source,
    annotationAttachments,
    annotationsOpacity,
    colorLegend,
  ]);

  /**
   * Mode contract:
   * - SELECT: pan/zoom the slide; clicks select and edit annotations.
   * - DRAW: every drag draws a shape; the slide never pans or wheel-zooms.
   * - MEASURE: clicks place measurement points; the slide does not pan.
   */
  useEffect(() => {
    if (!annoRef.current) {
      return;
    }

    annoRef.current.disableSelect = mode !== VIEWER_MODES.SELECT;
    annoRef.current.setDrawingEnabled(mode === VIEWER_MODES.DRAW);
  }, [mode, anno]);

  useEffect(() => {
    if (!annoRef.current) {
      return;
    }

    annoRef.current.setDrawingTool(
      annotationDrawTool === 'polygon'
        ? DEFAULT_ANNOTATION_DRAW_TOOL
        : annotationDrawTool,
    );
  }, [annotationDrawTool, anno]);

  // While a tool is active, dragging must never pan the slide.
  useEffect(() => {
    const viewer = viewerRef.current;

    if (!viewer) {
      return undefined;
    }

    const preventPanWhileToolActive = (event) => {
      if (mode !== VIEWER_MODES.SELECT) {
        event.preventDefaultAction = true;
      }
    };

    viewer.addHandler('canvas-drag', preventPanWhileToolActive);

    return () => {
      viewer.removeHandler('canvas-drag', preventPanWhileToolActive);
    };
  }, [mode, viewerState]);

  // With a tool active the wheel must neither zoom the slide nor scroll the page.
  useEffect(() => {
    const container = viewerRef.current?.container;

    if (!container) {
      return undefined;
    }

    const blockWheelWhileToolActive = (event) => {
      if (mode !== VIEWER_MODES.SELECT) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    container.addEventListener('wheel', blockWheelWhileToolActive, {
      capture: true,
      passive: false,
    });

    return () => {
      container.removeEventListener('wheel', blockWheelWhileToolActive, {
        capture: true,
      });
    };
  }, [mode, viewerState]);

  return (
    <Box
      className={`${classes.osdContainer} ${
        isCrosshairCursor && classes.crosshairCursor
      }`}
    >
      <Box className={osdDivClass}>
        <Modal open={openInFullPage} className={classes.modalIndex}>
          <Box className={classes.fullScreenModal}>
            <Box />
          </Box>
        </Modal>
        {showCollapsedNavigator && (
          <CollapsedBar
            onExpand={() => setIsNavigatorCollapsed(false)}
            preferencesField={NAVIGATOR_SWITCH_FIELD}
            title="Navigator"
            positionClass={classes.collapsedNavigator}
          />
        )}
        {showFullNavigator && (
          <div className={`navigator-wrapper c-shadow ${classes.navigator}`}>
            <Box
              className={`${classes.closeBackground} hover-visible`}
              onClick={() => setIsNavigatorCollapsed(true)}
            >
              <Close className={classes.closeIcon} />
            </Box>
            <div
              id={navigatorId}
              className="osd-navigator-container rounded-border"
            />
          </div>
        )}
        <Box id={`toolbar-${id}`} className="osd-tb">
          <CustomMultiSlideToolbar
            id={id}
            viewer={viewerState}
            isFullScreen={getIsFullScreen(viewerState)}
          />
        </Box>
        <Box className="openseadragon" id={id} />

        {/*
          The rail and the banner live inside OpenSeadragon's own element:
          full-page mode moves that element to the body and hides everything
          else, so siblings would disappear with the chrome.
        */}
        {isAnnotationUxV2Enabled &&
          viewerState?.element &&
          createPortal(
            <>
              <ViewerModeRail annotationsCount={annotations.length} />
              {annotationsEnabled && (
                <ViewerModeBanner
                  mode={mode}
                  drawTool={annotationDrawTool}
                  hasSelection={!!selectedAnnotation}
                  onExit={handleExitAnnotationMode}
                />
              )}
            </>,
            viewerState.element,
          )}

        <BrowserView>
          {CustomScreenshotButton && (
            <Box onClick={handleScreenshot}>
              <CustomScreenshotButton />
            </Box>
          )}
        </BrowserView>
      </Box>
    </Box>
  );
};

export const SimpleSlideViewMemo = React.memo(SimpleSlideView);
